import { Injectable, NgZone, ApplicationRef } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Modal Types
 */
export enum ModalType {
  ALERT = 'alert',           // Simple alert with OK button
  CONFIRMATION = 'confirmation', // Yes/No confirmation
  INFO = 'info',              // Info message, auto-dismiss or OK
  WARNING = 'warning',          // Warning message, can be styled differently
  PROMPT = 'prompt'             // Modal with text input field
}

/**
 * Modal Size Variants
 */
export enum ModalSize {
  SMALL = 'small',   // For alerts and simple messages
  MEDIUM = 'medium', // Default size
  LARGE = 'large'    // For detailed content
}

/**
 * Modal Configuration Interface
 */
export interface ModalConfig {
  type: ModalType;
  size?: ModalSize;
  title?: string;
  compactHeader?: boolean; // If true, render a denser header layout for content-heavy modals
  message: string;
  messageAlignLeft?: boolean; // If true, render message as left-aligned content (useful for JSON payloads)
  hideInfoIcon?: boolean; // If true, suppress success/info icon for ALERT/INFO modals
  showCopyButton?: boolean; // If true, show a copy button in modal header
  copyText?: string; // Optional explicit text to copy (defaults to message)
  showHeader?: boolean;
  showCloseButton?: boolean;
  details?: { label: string; value: string }[]; // Optional key-value details
  confirmText?: string;  // Text for confirm button (default: "Yes")
  cancelText?: string;   // Text for cancel button (default: "No")
  autoDismiss?: boolean; // Auto-dismiss after timeout
  autoDismissTime?: number; // Time in ms (default: 3000)
  onConfirm?: () => void; // Callback for confirm action
  onCancel?: () => void;  // Callback for cancel action
  allowOutsideClick?: boolean; // Allow closing by clicking overlay
  allowEscapeKey?: boolean;    // Allow closing with ESC key
  priority?: number; // Priority for queue (higher = more important)
  isDanger?: boolean; // If true, confirm button will be styled as danger (red)
  isListLayout?: boolean; // If true, show buttons as vertical list (for format pickers)
  inputPlaceholder?: string; // Placeholder for text input (PROMPT type)
  inputValue?: string; // Initial value for text input (PROMPT type)
  inputMultiline?: boolean; // If true (PROMPT type), render a <textarea> instead of <input>
  inputRows?: number; // Rows for the textarea when inputMultiline is true (default 4)
  requireInputChange?: boolean; // If true (PROMPT type), disable confirm until value is non-empty AND differs from inputValue
  onSubmit?: (value: string) => void; // Callback for prompt submission
}

/**
 * Modal State Interface
 */
interface ModalState {
  isOpen: boolean;
  config: ModalConfig | null;
  queueLength?: number; // Track queue size
}

/**
 * Queued Modal Entry
 */
interface QueuedModal {
  config: ModalConfig;
  timestamp: number;
  priority: number;
}

/**
 * Production-Ready Modal Service
 * Centralized service for managing modal dialogs with guaranteed rendering
 * 
 * Features:
 * - Zone.js integration for guaranteed Angular rendering
 * - Modal queue system for handling rapid triggers
 * - Explicit change detection triggering
 * - Memory leak prevention (proper timer cleanup)
 * - Error recovery mechanisms
 */
@Injectable({
  providedIn: 'root'
})
export class ModalService {
  // State management
  private modalState = new BehaviorSubject<ModalState>({
    isOpen: false,
    config: null,
    queueLength: 0
  });
  public modalState$: Observable<ModalState> = this.modalState.asObservable();

  // Force update subject for explicit change detection triggers
  private forceUpdate$ = new Subject<void>();
  public forceUpdate = this.forceUpdate$.asObservable();

  // Modal queue for handling multiple rapid triggers
  private modalQueue: QueuedModal[] = [];
  
  // Double-click protection flag
  private isProcessing = false;
  private isProcessingQueue = false;

  // Timer tracking for proper cleanup
  private activeTimers: Set<any> = new Set();

  constructor(
    private ngZone: NgZone,
    private appRef: ApplicationRef
  ) {}

  /**
   * Show a modal with the given configuration
   * GUARANTEED to render using Zone.js and explicit change detection
   */
  show(config: ModalConfig): void {
    // Run inside Angular zone to guarantee change detection
    this.ngZone.run(() => {
      const defaultConfig: Partial<ModalConfig> = {
        size: ModalSize.MEDIUM,
        showHeader: true,
        showCloseButton: true,
        confirmText: 'Yes',
        cancelText: 'No',
        autoDismiss: false,
        autoDismissTime: 3000,
        allowOutsideClick: true,
        allowEscapeKey: true,
        priority: 0
      };

      const mergedConfig = { ...defaultConfig, ...config } as ModalConfig;

      // Check if a modal is already open
      const currentState = this.modalState.value;
      if (currentState.isOpen) {
        this.addToQueue(mergedConfig);
        return;
      }

      // Show modal immediately
      this.displayModal(mergedConfig);
    });
  }

  /**
   * Display modal immediately (internal method)
   */
  private displayModal(config: ModalConfig): void {
    // Clear any active timers first
    this.clearActiveTimers();

    // Update state
    this.modalState.next({
      isOpen: true,
      config: config,
      queueLength: this.modalQueue.length
    });

    // Force change detection
    this.forceUpdate$.next();
    this.appRef.tick();

    // Setup auto-dismiss if configured
    if (config.autoDismiss && config.autoDismissTime) {
      const timer = setTimeout(() => {
        this.close();
      }, config.autoDismissTime);
      
      this.activeTimers.add(timer);
    }
  }

  /**
   * Add modal to queue for later display
   */
  private addToQueue(config: ModalConfig): void {
    const queueEntry: QueuedModal = {
      config,
      timestamp: Date.now(),
      priority: config.priority || 0
    };

    this.modalQueue.push(queueEntry);
    
    // Sort by priority (higher first), then by timestamp (older first)
    this.modalQueue.sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.timestamp - b.timestamp;
    });

    // Update queue length in state
    this.modalState.next({
      ...this.modalState.value,
      queueLength: this.modalQueue.length
    });
  }

  /**
   * Process next modal in queue
   */
  private processQueue(): void {
    if (this.isProcessingQueue || this.modalQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    // Small delay to prevent visual glitches when closing and opening rapidly
    setTimeout(() => {
      const nextModal = this.modalQueue.shift();
      if (nextModal) {
        this.displayModal(nextModal.config);
      }
      this.isProcessingQueue = false;
    }, 100);
  }

  /**
   * Close the modal
   * Runs inside Angular zone for guaranteed change detection
   */
  close(): void {
    this.ngZone.run(() => {
      // Clear timers
      this.clearActiveTimers();

      // Update state
      this.modalState.next({
        isOpen: false,
        config: null,
        queueLength: this.modalQueue.length
      });

      // Force change detection
      this.forceUpdate$.next();
      this.appRef.tick();

      // Process queue after a short delay
      setTimeout(() => this.processQueue(), 150);
    });
  }

  /**
   * Clear all active timers
   */
  private clearActiveTimers(): void {
    this.activeTimers.forEach(timer => clearTimeout(timer));
    this.activeTimers.clear();
  }

  /**
   * Handle confirm action with double-click protection
   * Executes callback inside Angular zone
   */
  confirm(): void {
    this.ngZone.run(() => {
      // Guard: Prevent double-execution (production-grade protection)
      if (this.isProcessing) {
        return;
      }
      
      this.isProcessing = true;
      const currentState = this.modalState.value;
      
      if (currentState.config?.onConfirm) {
        try {
          currentState.config.onConfirm();
        } catch (error) {
        }
      }
      
      this.close();
      
      // Reset processing flag after modal closes
      setTimeout(() => {
        this.isProcessing = false;
      }, 200);
    });
  }

  /**
   * Handle cancel action with double-click protection
   * Executes callback inside Angular zone
   */
  cancel(): void {
    this.ngZone.run(() => {
      // Guard: Prevent double-execution (production-grade protection)
      if (this.isProcessing) {
        return;
      }
      
      this.isProcessing = true;
      const currentState = this.modalState.value;
      
      if (currentState.config?.onCancel) {
        try {
          currentState.config.onCancel();
        } catch (error) {
        }
      }
      
      this.close();
      
      // Reset processing flag after modal closes
      setTimeout(() => {
        this.isProcessing = false;
      }, 200);
    });
  }

  /**
   * Clear modal queue (useful for cleanup or reset)
   */
  clearQueue(): void {
    this.modalQueue = [];
    this.modalState.next({
      ...this.modalState.value,
      queueLength: 0
    });
  }

  /**
   * Get current queue length
   */
  getQueueLength(): number {
    return this.modalQueue.length;
  }

  /**
   * Cleanup method - call on application destroy
   */
  destroy(): void {
    this.clearActiveTimers();
    this.clearQueue();
    this.close();
  }

  // ========== SCENARIO-SPECIFIC HELPERS ==========

  /**
   * Scenario 1: Run Initiated Alert
   */
  /**
   * Scenario 2: Run Completed Alert
   */
  showRunCompleted(evalId: string, documentName: string): void {
    this.show({
      type: ModalType.ALERT,
      size: ModalSize.SMALL,
      message: 'Run Successfully Completed!',
      showHeader: false,
      confirmText: 'OK',
      details: [
        { label: 'Eval ID', value: evalId },
        { label: 'Document Name', value: documentName }
      ]
    });
  }

  /**
   * Scenario 3: Logout Confirmation
   */
  showLogoutConfirmation(onConfirm: () => void, customMessage?: string): void {
    this.show({
      type: ModalType.CONFIRMATION,
      size: customMessage ? ModalSize.MEDIUM : ModalSize.SMALL, // Use MEDIUM for detailed warnings
      title: 'Confirm Logout',
      message: customMessage || 'Do you want to Log Out?',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: onConfirm
    });
  }

  /**
   * Scenario 4: File Upload Confirmation
   */
  showUploadConfirmation(fileName: string, onConfirm: () => void, onCancel?: () => void): void {
    this.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Confirm Upload',
      message: 'Do you want to upload the document?',
      details: [{ label: 'File', value: fileName }],
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: onConfirm,
      onCancel: onCancel
    });
  }

  /**
   * Scenario 6: Save Changes Confirmation (Ground Truth)
   */
  showSaveConfirmation(onConfirm: () => void, onCancel?: () => void): void {
    this.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Unsaved Changes',
      message: 'Do you want to save changes?',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: onConfirm,
      onCancel: onCancel,
      allowOutsideClick: false, // Prevent accidental dismissal
      allowEscapeKey: false
    });
  }

  /**
   * Generic Error Alert
   */
  showError(message: string, details?: string): void {
    this.show({
      type: ModalType.ALERT,
      size: ModalSize.SMALL,
      title: 'Error',
      message: message,
      details: details ? [{ label: 'Details', value: details }] : undefined,
      confirmText: 'OK'
    });
  }

  /**
   * Generic Success Alert
   */
  showSuccess(message: string, autoDismiss: boolean = true): void {
    this.show({
      type: ModalType.ALERT,
      size: ModalSize.SMALL,
      message: message,
      showHeader: false,
      autoDismiss: autoDismiss,
      autoDismissTime: 2000,
      confirmText: 'OK'
    });
  }

  /**
   * Generic Confirmation
   */
  showConfirmation(
    message: string,
    onConfirm: () => void,
    options?: {
      title?: string;
      confirmText?: string;
      cancelText?: string;
      details?: { label: string; value: string }[];
    }
  ): void {
    this.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: options?.title || 'Confirm Action',
      message: message,
      confirmText: options?.confirmText || 'Yes',
      cancelText: options?.cancelText || 'No',
      details: options?.details,
      onConfirm: onConfirm
    });
  }

  /**
   * Prompt modal with text input
   * @param message - The question/instruction to display
   * @param onSubmit - Callback function that receives the entered text
   * @param options - Optional configuration
   */
  showPrompt(
    message: string,
    onSubmit: (value: string) => void,
    options?: {
      title?: string;
      placeholder?: string;
      initialValue?: string;
      confirmText?: string;
      cancelText?: string;
      inputMultiline?: boolean;
      inputRows?: number;
      requireInputChange?: boolean;
    }
  ): void {
    this.show({
      type: ModalType.PROMPT,
      size: ModalSize.MEDIUM,
      title: options?.title || 'Input Required',
      message: message,
      inputPlaceholder: options?.placeholder || '',
      inputValue: options?.initialValue || '',
      inputMultiline: options?.inputMultiline || false,
      inputRows: options?.inputRows,
      requireInputChange: options?.requireInputChange || false,
      confirmText: options?.confirmText || 'Submit',
      cancelText: options?.cancelText || 'Cancel',
      onSubmit: onSubmit,
      allowOutsideClick: false,
      allowEscapeKey: true
    });
  }
}
